import { ChangeEvent } from 'react';

import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import { Field } from '@atlaskit/form';
import EmailIcon from '@atlaskit/icon/core/email';
import ErrorIcon from '@atlaskit/icon/core/status-error';
import SuccessIcon from '@atlaskit/icon/core/status-success';
import TextField from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';

import { useAccountCreationContext } from '@/components/signup/account-creation/hooks/account-creation-context';
import { wacToken } from '@/libraries/design/foundations';

/** Inline embedded signup email field: envelope icon + diamond status-error (Figma). */
export const InlineEmbeddedSignupInput = ({
	email,
	setEmail,
	isValid,
	errorMessageText,
}: {
	email: string;
	setEmail: React.Dispatch<React.SetStateAction<string>>;
	isValid: boolean | undefined;
	errorMessageText: string;
}) => {
	// Validation (isValid / setIsEmailValid) is owned by InlineEmbeddedSignup
	const { setIsReadyForInvalid, setTypeAheadEmail } = useAccountCreationContext();
	const intl = useIntl();

	const placeholderText = intl.formatMessage({
		id: 'BxpUiSignup.SignupForm.enterWorkEmail',
		defaultMessage: 'Enter work email',
	});

	const validMessage = intl.formatMessage({
		id: 'gwp.accountSignin.validEmailIcon',
		defaultMessage: 'Email is valid',
	});

	// Border/focus/valid/invalid chrome lives on InlineEmbeddedSignup field wrappers
	return (
		<div css={containerCss}>
			<Field aria-required={true} isRequired label="" name="email">
				{({ fieldProps }) => (
					<div css={[inlineInputCss, isValid !== undefined && inlineInputWithStatusCss]}>
						<span css={emailIconCss} aria-hidden="true">
							<EmailIcon label="" />
						</span>
						<TextField
							{...fieldProps}
							autoComplete="email"
							id="inline_embedded_signup_email_input"
							aria-label={placeholderText}
							onBlur={() => setIsReadyForInvalid(true)}
							onChange={(e: ChangeEvent<HTMLInputElement>) => {
								setEmail(e.target.value);
								setTypeAheadEmail(e.target.value);
								fieldProps.onChange(e);
							}}
							placeholder={placeholderText}
							testId="email_field"
							type="email"
							value={email}
						/>
						{isValid === false ? (
							<div css={[iconCss, invalidIconCss]}>
								<ErrorIcon label={errorMessageText} />
							</div>
						) : isValid === true ? (
							<div css={[iconCss, validIconCss]}>
								<SuccessIcon label={validMessage} />
							</div>
						) : null}
					</div>
				)}
			</Field>
		</div>
	);
};

const containerCss = css({
	height: '100%',
	boxSizing: 'border-box',
	width: '100%',
	display: 'flex',
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- override Field wrapper spacing
	['> div']: {
		margin: 0,
		padding: 0,
		height: '100%',
		width: '100%',
	},
});

const inlineInputCss = css({
	display: 'flex',
	alignItems: 'center',
	height: '100%',
	width: '100%',
	// Grow the real <input> so browser/extension autofill icons aren't mid-pill.
	// Email→input spacing is on emailIconCss (no flex gap — keeps autofill↔error tight).
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['input:-webkit-autofill:focus']: {
		['transition']: 'background-color 5000s ease-in-out 0s',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['> div:first-of-type']: {
		flex: '1 1 auto',
		minWidth: 0,
		width: '100%',
		overflow: 'hidden',
		backgroundColor: 'transparent !important',
		border: 'none',
		borderRadius: 0,
		boxShadow: 'none !important',
		height: '100%',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['input']: {
		height: '100%',
		width: '100%',
		background: 'transparent',
		color: wacToken('color.primary.black'),
		fontFamily: wacToken('font.charlie.text'),
		fontSize: '18px',
		overflow: 'hidden',
		paddingInlineStart: '0 !important',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		fontWeight: 300,
		display: 'flex',
		alignItems: 'center',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['input::placeholder']: {
		color: wacToken('color.neutral.light600'),
		fontSize: '18px',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['input:focus, input:focus-visible']: {
		background: 'transparent !important',
		backgroundColor: 'transparent !important',
		boxShadow: 'none !important',
		outline: 'none !important',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['> div:first-of-type:focus-within']: {
		boxShadow: 'none !important',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['span']: {
		alignSelf: 'center',
	},
});

/** Keep 1Password/autofill tight to the status icon */
const inlineInputWithStatusCss = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['input']: {
		paddingInlineEnd: '0 !important',
	},
});

const emailIconCss = css({
	display: 'inline-flex',
	flexShrink: 0,
	marginInlineEnd: token('space.100'),
	color: wacToken('color.primary.black'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['svg']: {
		height: '18px',
		width: '18px',
	},
});

/**
 * Figma status icons: 18×18, no extra end padding (field chrome supplies 16px).
 * White background covers autofill when icons sit close.
 */
const iconCss = css({
	display: 'flex',
	alignSelf: 'center',
	flexShrink: 0,
	position: 'relative',
	zIndex: 1,
	backgroundColor: wacToken('color.neutral.light0'),
	borderRadius: '50%',
	lineHeight: 0,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['svg']: {
		height: '18px',
		width: '18px',
	},
});

const invalidIconCss = css({
	color: token('color.icon.danger'),
});

const validIconCss = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['svg']: {
		color: wacToken('color.extended.lime600'),
	},
});
